import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import type { QuantityControlProps } from "./QuantityControl.types";
import { MAX_CART_QUANTITY } from "@/core/orders/constants";

const QuantityControl: React.FC<QuantityControlProps> = ({
  quantity,
  onQuantityChange,
  min = 1,
  // Дефолт — контрактный потолок, а не круглое число: единственный
  // потребитель контрола (PreorderCartItem) шлёт заказы, и 99 здесь молча
  // ограничивал корзину строже, чем требует домен.
  max = MAX_CART_QUANTITY,
  disabled = false,
  "aria-label": ariaLabel = "Quantity control",
  size = "medium",
}) => {
  const theme = useTheme();

  const handleDecrease = useCallback(() => {
    if (quantity > min && !disabled) {
      onQuantityChange(quantity - 1);
    }
  }, [quantity, min, disabled, onQuantityChange]);

  const handleIncrease = useCallback(() => {
    if (quantity < max && !disabled) {
      onQuantityChange(quantity + 1);
    }
  }, [quantity, max, disabled, onQuantityChange]);

  const isDecreaseDisabled = disabled || quantity <= min;
  const isIncreaseDisabled = disabled || quantity >= max;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.action.hover,
        borderRadius: 3,
        height: size === "small" ? 32 : 40,
        width: size === "small" ? 80 : 100,
        padding: "3px",
        gap: 0.5,
      }}
      role="group"
      aria-label={ariaLabel}
    >
      <IconButton
        onClick={handleDecrease}
        disabled={isDecreaseDisabled}
        size={size === "small" ? "small" : "medium"}
        aria-label="Decrease quantity"
        sx={{
          minWidth: size === "small" ? 20 : 24,
          width: size === "small" ? 20 : 24,
          height: size === "small" ? 20 : 24,
          borderRadius: "50%",
          color: theme.palette.text.primary,
          opacity: isDecreaseDisabled ? 0.6 : 1,
          "&:hover": {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        <RemoveIcon sx={{ fontSize: size === "small" ? 12 : 16 }} />
      </IconButton>

      <Typography
        variant={size === "small" ? "caption" : "body2"}
        sx={{
          fontWeight: 500,
          color: theme.palette.text.primary,
          fontSize: size === "small" ? "14px" : "16px",
          textAlign: "center",
          minWidth: size === "small" ? 20 : 24,
          userSelect: "none",
        }}
        role="status"
        aria-live="polite"
        aria-label={`Quantity: ${quantity}`}
      >
        {quantity}
      </Typography>

      <IconButton
        onClick={handleIncrease}
        disabled={isIncreaseDisabled}
        size={size === "small" ? "small" : "medium"}
        aria-label="Increase quantity"
        sx={{
          minWidth: size === "small" ? 20 : 24,
          width: size === "small" ? 20 : 24,
          height: size === "small" ? 20 : 24,
          borderRadius: "50%",
          color: theme.palette.text.primary,
          opacity: isIncreaseDisabled ? 0.6 : 1,
          "&:hover": {
            backgroundColor: theme.palette.action.selected,
          },
        }}
      >
        <AddIcon sx={{ fontSize: size === "small" ? 12 : 16 }} />
      </IconButton>
    </Box>
  );
};

export default QuantityControl;
